// SEO utility for updating page meta tags dynamically
export function updatePageMeta(options: {
    title?: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    canonicalUrl?: string;
    keywords?: string;
    twitterCard?: string;
}) {
    // Update title
    if (options.title) {
        document.title = options.title;
    }

    const updateMeta = (name: string, content: string, property = false) => {
        const attribute = property ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute(attribute, name);
            document.head.appendChild(meta);
        }
        meta.content = content;
    };

    if (options.description) {
        updateMeta('description', options.description);
    }
    if (options.keywords) {
        updateMeta('keywords', options.keywords);
    }
    if (options.twitterCard) {
        updateMeta('twitter:card', options.twitterCard);
    }
    if (options.ogTitle) {
        updateMeta('og:title', options.ogTitle, true);
        updateMeta('twitter:title', options.ogTitle);
    }
    if (options.ogDescription) {
        updateMeta('og:description', options.ogDescription, true);
        updateMeta('twitter:description', options.ogDescription);
    }
    if (options.ogImage) {
        updateMeta('og:image', options.ogImage, true);
        updateMeta('twitter:image', options.ogImage);
    }

    // Update canonical URL
    if (options.canonicalUrl) {
        let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
        if (!link) {
            link = document.createElement('link');
            link.rel = 'canonical';
            document.head.appendChild(link);
        }
        link.href = options.canonicalUrl;
    }
}

// Add JSON-LD structured data
export function addStructuredData(data: object) {
    const existing = document.querySelector('script[type="application/ld+json"]');
    if (existing) {
        existing.remove();
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
}

// Generate blog post structured data
export function generateBlogPostStructuredData(post: {
    title: string;
    excerpt: string;
    date: string;
    author: string;
    heroImage?: string;
    slug: string;
}) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
        datePublished: post.date,
        dateModified: post.date,
        author: {
            '@type': 'Organization',
            name: post.author || 'SwapGifts Editorial Team',
        },
        publisher: {
            '@type': 'Organization',
            name: 'SwapGifts',
            logo: {
                '@type': 'ImageObject',
                url: 'https://swapgifts.store/logo.png',
            },
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://swapgifts.store/blog/${post.slug}`,
        },
    };
}

// Generate Organization structured data
export function generateOrganizationStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'SwapGifts',
        url: 'https://swapgifts.store',
        logo: 'https://swapgifts.store/logo.png',
        description: 'Professional platform for organizing Secret Santa, office parties, and family gift exchanges.',
        sameAs: [
            'https://twitter.com/swapgifts',
            'https://facebook.com/swapgifts',
        ],
    };
}

// Generate WebSite structured data
export function generateWebSiteStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'SwapGifts',
        url: 'https://swapgifts.store',
        description: 'Organize Secret Santa and gift exchanges in minutes. Free, secure, and easy to use.',
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://swapgifts.store/gift-finder?q={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
        },
    };
}

// Apply SEO metadata based on route path
export function applySEOForPath(path: string) {
    let title = 'SwapGifts – Gift Exchange Platform';
    let description = 'Discover and exchange gifts for any occasion, including Secret Santa, birthdays, and holidays.';
    const ogImage = 'https://swapgifts.store/default-og-image.jpg';
    const canonicalUrl = `https://swapgifts.store${path}`;

    if (path.startsWith('/events/')) {
        const event = path.split('/')[2];
        title = `SwapGifts – ${event.charAt(0).toUpperCase() + event.slice(1)} Event`;
        description = `Join the ${event} gift exchange on SwapGifts.`;
    } else if (path === '/blog') {
        title = 'SwapGifts Blog – Gift Ideas & Tips';
        description = 'Read the latest articles about gift ideas, holiday guides, and Secret Santa tips.';
    } else if (path.startsWith('/blog/')) {
        title = 'SwapGifts Blog Post';
        description = 'Read this blog post on SwapGifts.';
    } else if (path === '/faq') {
        title = 'SwapGifts FAQ';
        description = 'Frequently asked questions about using SwapGifts.';
    } else if (path === '/terms') {
        title = 'Terms of Service – Aritofu Labs';
        description = 'Terms of Service for SwapGifts.store, operated by Aritofu Labs.';
    } else if (path === '/refund-policy') {
        title = 'Refund Policy – Aritofu Labs';
        description = 'Refund policy for digital subscriptions and products by Aritofu Labs.';
    } else if (path === '/autoaccept') {
        title = 'AutoAccept Antigravity – VS Code Extension by Aritofu Labs';
        description = 'Automatically accept AI agent actions in VS Code. Zero config, no babysitting required. Install free or upgrade to Pro.';
    }

    updatePageMeta({
        title,
        description,
        ogTitle: title,
        ogDescription: description,
        ogImage,
        canonicalUrl,
        twitterCard: 'summary_large_image',
    });
}
